/* eslint-disable react/no-array-index-key */
import React, { Component } from 'react';
import propTypes from 'prop-types';
import { Button, Input, InputGroup, InputGroupAddon, Label } from 'reactstrap';
import shortid from 'shortid';

import ConfirmationModal from 'app/ConfirmationModal/components/ConfirmationModal';
import Chip from 'app/Chip/components/Chip';
import sharedStyles from 'app/shared.css';
import { utcToPtDateString } from 'lib/date';
import { createTags } from 'lib/string';
import { validatePageName } from 'lib/nameValidations';
import { noop } from 'lib/noop';
import { IconButton } from 'ui/Components';
import { favorite, unfavorite } from 'ui/Icons';

import ExperimentDetails from './ExperimentDetails';

/**
 * render a form component for component names and tagging
 */
export default class NameAndTags extends Component {
  static propTypes = {
    clearExperiment: propTypes.func,
    experimentName: propTypes.string,
    fetchExperiment: propTypes.func,
    id: propTypes.string,
    isDynamic: propTypes.bool,
    isExperiment: propTypes.bool,
    isFavorite: propTypes.bool,
    isLive: propTypes.bool,
    isReadonly: propTypes.bool,
    name: propTypes.string.isRequired,
    minTagInputLength: propTypes.number,
    modify: propTypes.func.isRequired,
    onFavoriteClick: propTypes.func,
    onNameChange: propTypes.func,
    schema: propTypes.object.isRequired,
    showFavoriteIcon: propTypes.bool,
    startDate: propTypes.string,
    tags: propTypes.shape({
      NameTags: propTypes.arrayOf(propTypes.string),
      UserTags: propTypes.arrayOf(propTypes.string)
    }).isRequired
  };

  static defaultProps = {
    clearExperiment: noop,
    experimentName: '',
    fetchExperiment: noop,
    id: '',
    isDynamic: false,
    isExperiment: false,
    isFavorite: false,
    isLive: false,
    isReadonly: false,
    minTagInputLength: 2,
    onFavoriteClick: noop,
    onNameChange: noop,
    showFavoriteIcon: false,
    startDate: ''
  };

  /**
   * constructor for this component
   * @param {object} props - props to this component
   */
  constructor(props) {
    super(props);

    this.state = {
      name: props.name || '',
      tagText: '',
      failedValidations: []
    };

    this.handleNameChange = this.handleNameChange.bind(this);
    this.handleTextInputChange = this.handleTextInputChange.bind(this);
    this.handleUserTagAdd = this.handleUserTagAdd.bind(this);
    this.hideValidationModal = this.hideValidationModal.bind(this);
  }

  /**
   * change name
   * @param {object} event - event containing the new component name
   */
  handleNameChange(event) {
    const { id, name, onNameChange, schema, modify } = this.props;
    const { name: nameFromState } = this.state;

    const newName = event.target.value.trim();
    const NameTags = createTags(newName);
    const failedValidations = validatePageName(nameFromState);
    this.setState({ failedValidations });
    if (name !== newName) {
      onNameChange(newName);
      modify(NameTags, schema.nameTags, id);
      modify(newName, schema.name, id);
    }
  }

  /**
   * handles delete user tags event
   * @param {string} tag - tag text being deleted
   */
  handleDeleteTag(tag) {
    const { id, modify, schema, tags } = this.props;
    const newUserTags = tags.UserTags.filter(uTagText => uTagText !== tag);
    modify(newUserTags, schema.userTags, id);
  }

  /**
   * update internal state for text inputs
   * @param {object} event - text input event
   */
  handleTextInputChange(event) {
    const { name, value } = event.target;
    this.setState({ [name]: value });
  }

  /**
   * add user tag and clear tag input
   */
  handleUserTagAdd() {
    const {
      id,
      minTagInputLength,
      modify,
      schema,
      tags: { UserTags: userTags = [] }
    } = this.props;
    const { tagText } = this.state;
    const trimmedTag = tagText.trim();

    if (trimmedTag.length >= minTagInputLength && !userTags.includes(trimmedTag)) {
      modify([...userTags, trimmedTag], schema.userTags, id);
    }

    this.setState({ tagText: '' });
  }

  /**
   * hides validation message modal on ok click
   */
  hideValidationModal() {
    this.setState({ failedValidations: [] });
  }

  /**
   * render information about scheduled/live dynamic component
   * @returns {React.Component} information about dynamic component
   */
  renderLiveInfo() {
    const { isLive, startDate } = this.props;

    const isScheduled = !!startDate && new Date().toISOString() < startDate;

    // no info to show
    if (!isLive && !isScheduled) {
      return null;
    }

    return (
      <div data-test-id="nametags-live-date">
        <Label className="font-weight-bold mr-2">
          {isLive ? 'Live now as of:' : 'Live date scheduled for:'}
        </Label>
        {utcToPtDateString(startDate, 'L LT z')}
      </div>
    );
  }

  /**
   * renders a OkConfirmationModal
   * @returns {React.Component} modal with ok button
   */
  renderValidationMessage() {
    const { failedValidations } = this.state;

    // FIXME style to match comps when they are available
    return (
      <ConfirmationModal
        isOpen={failedValidations.length > 0}
        onOkClick={this.hideValidationModal}
        data-test-id="nameAndTags-okConfirmationModal"
      >
        <ul className="p-4">
          {failedValidations.map((message, index) => (
            <li key={index}>
              <span className={sharedStyles.validationMessage}>{message}</span>
            </li>
          ))}
        </ul>
      </ConfirmationModal>
    );
  }

  render() {
    const {
      clearExperiment,
      experimentName,
      fetchExperiment,
      id,
      isDynamic,
      isExperiment,
      isFavorite,
      isReadonly,
      modify,
      onFavoriteClick,
      showFavoriteIcon,
      tags
    } = this.props;
    return (
      <div className="p-4">
        <div className="d-flex justify-content-between align-items-top">
          <Label className="font-weight-bold">Name</Label>
          {showFavoriteIcon && (
            <IconButton
              data-test-id="nametags-favorite-icon"
              id={this.id}
              icon={isFavorite ? favorite : unfavorite}
              isReadonly={isReadonly}
              noMargin
              onClick={onFavoriteClick}
              title={isFavorite ? 'Mark Unfavorite' : 'Mark Favorite'}
              rem={2.4}
            />
          )}
        </div>
        <Input
          className="rounded"
          name="name"
          type="text"
          value={this.state.name}
          onChange={this.handleTextInputChange}
          onBlur={this.handleNameChange}
          data-test-id="nametags-name-input"
          disabled={isReadonly}
        />
        <Label className="font-weight-bold mt-3">Tagging</Label>
        <InputGroup>
          <Input
            className="rounded-left"
            name="tagText"
            type="text"
            value={this.state.tagText}
            onChange={this.handleTextInputChange}
            placeholder="Add Tag..."
            data-test-id="nametags-tag-input"
            disabled={isReadonly}
            onKeyPress={event => {
              if (event.key === 'Enter') {
                this.handleUserTagAdd();
              }
            }}
          />
          <InputGroupAddon className="rounded-right" addonType="append">
            <Button
              className="rounded-right px-4"
              color="primary"
              disabled={isReadonly}
              onClick={this.handleUserTagAdd}
              data-test-id="nametags-tag-add-button"
            >
              +
            </Button>
          </InputGroupAddon>
        </InputGroup>
        <div className="d-flex flex-wrap mt-3">
          {tags.NameTags.map(text => (
            <Chip
              key={shortid.generate()}
              className="mr-2 mb-2"
              leftColor="info"
              rightColor="info"
              rightText={text}
              hideClose
              isReadonly={isReadonly}
            />
          ))}
          {tags.UserTags.map(text => (
            <Chip
              key={shortid.generate()}
              className="mr-2 mb-2"
              leftColor="primary"
              rightColor="primary"
              rightText={text}
              onClose={() => this.handleDeleteTag(text)}
              isReadonly={isReadonly}
            />
          ))}
        </div>
        {isDynamic && this.renderLiveInfo()}
        {isExperiment && (
          <ExperimentDetails
            experimentName={experimentName}
            clearExperiment={clearExperiment}
            fetchExperiment={fetchExperiment}
            id={id}
            isReadonly={isReadonly}
            modify={modify}
          />
        )}
        {this.renderValidationMessage()}
      </div>
    );
  }
}
